import { EllipsisIcon, XIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { InboxAction } from '@/lib/api/generated/model';

import { PAGE_COPY } from '../copy';

/**
 * What the chosen rows can have done to them (spec 147 US2): Mark read, Mark
 * unread, Archive (Unarchive in Archived), "Select all on this page", then
 * "Select all {n} matching", which switches to the whole-filter actions. Its
 * count is a live region, so a screen reader hears each change. On a phone
 * it sits at the bottom of the screen and its actions fold into a menu.
 */
export function SelectionBar({
  count,
  archived,
  allOnPage,
  matching,
  allMatching,
  busy,
  onAction,
  onSelectPage,
  onSelectMatching,
  onFilterAction,
  onClear,
}: {
  count: number;
  archived: boolean;
  /** Every loaded row is selected. */
  allOnPage: boolean;
  /** How many the filter matches, once known; offered when more than loaded. */
  matching: number | undefined;
  allMatching: boolean;
  busy: boolean;
  onAction: (action: InboxAction) => void;
  onSelectPage: () => void;
  onSelectMatching: () => void;
  onFilterAction: (action: 'read' | 'archive') => void;
  onClear: () => void;
}) {
  return (
    <div
      role="region"
      aria-label="Selection"
      className="bg-card border-border shadow-raised flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-lg border px-3 py-2 max-md:fixed max-md:inset-x-3 max-md:bottom-3 max-md:z-30"
    >
      <span
        aria-live="polite"
        className="text-foreground me-1 text-sm font-medium tabular-nums"
      >
        {PAGE_COPY.selected(
          allMatching && matching !== undefined ? matching : count,
        )}
      </span>
      <div className="hidden md:contents">
        {allMatching ? (
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => {
                onFilterAction('read');
              }}
            >
              {PAGE_COPY.markAllMatching}
            </Button>
            {archived ? null : (
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => {
                  onFilterAction('archive');
                }}
              >
                {PAGE_COPY.archiveAllMatching}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => {
                onAction('read');
              }}
            >
              {PAGE_COPY.markRead}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => {
                onAction('unread');
              }}
            >
              {PAGE_COPY.markUnread}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => {
                onAction(archived ? 'unarchive' : 'archive');
              }}
            >
              {archived ? PAGE_COPY.unarchive : PAGE_COPY.archive}
            </Button>
            {!allOnPage ? (
              <Button variant="ghost" size="sm" onClick={onSelectPage}>
                {PAGE_COPY.selectPage}
              </Button>
            ) : matching !== undefined && matching > count ? (
              <Button variant="ghost" size="sm" onClick={onSelectMatching}>
                {PAGE_COPY.selectMatching(matching)}
              </Button>
            ) : null}
          </>
        )}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={<Button variant="outline" size="sm" className="md:hidden" />}
        >
          <EllipsisIcon aria-hidden="true" />
          Actions
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="top">
          {allMatching ? (
            <>
              <DropdownMenuItem
                disabled={busy}
                onClick={() => {
                  onFilterAction('read');
                }}
              >
                {PAGE_COPY.markAllMatching}
              </DropdownMenuItem>
              {archived ? null : (
                <DropdownMenuItem
                  disabled={busy}
                  onClick={() => {
                    onFilterAction('archive');
                  }}
                >
                  {PAGE_COPY.archiveAllMatching}
                </DropdownMenuItem>
              )}
            </>
          ) : (
            <>
              <DropdownMenuItem
                disabled={busy}
                onClick={() => {
                  onAction('read');
                }}
              >
                {PAGE_COPY.markRead}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={busy}
                onClick={() => {
                  onAction('unread');
                }}
              >
                {PAGE_COPY.markUnread}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={busy}
                onClick={() => {
                  onAction(archived ? 'unarchive' : 'archive');
                }}
              >
                {archived ? PAGE_COPY.unarchive : PAGE_COPY.archive}
              </DropdownMenuItem>
              {!allOnPage ? (
                <DropdownMenuItem onClick={onSelectPage}>
                  {PAGE_COPY.selectPage}
                </DropdownMenuItem>
              ) : matching !== undefined && matching > count ? (
                <DropdownMenuItem onClick={onSelectMatching}>
                  {PAGE_COPY.selectMatching(matching)}
                </DropdownMenuItem>
              ) : null}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <Button
        variant="ghost"
        size="icon-sm"
        className="ms-auto"
        aria-label="Clear selection"
        title="Clear selection"
        onClick={onClear}
      >
        <XIcon aria-hidden="true" />
      </Button>
    </div>
  );
}
